// Supply Network panel: stateful hook for the "App-using dealer
// locations & plant assignment" tab. Sister to
// useManufacturingPlantDealerLocations, which handles non-app dealer
// locations from mfg_dealer_locations. This one is for app-using
// subscriber dealers' own physical locations (public.locations,
// overlaid with mfg_subscriber_location_settings).
//
// Owns:
//   - data: subscriber locations (from /api/mfg/subscriber-locations)
//     + subscriber dealers (from /api/mfg/dealers) for the per-org
//     primary_plant_id inheritance hop.
//   - search + 3 filters (plant, subscriber org, state, active).
//   - multi-select state.
//   - unified confirm-on-reassign flow for both bulk and per-row.
//
// Plants come in as a prop so the panel shares one /api/mfg/plants
// fetch with the Plants list. Inheritance resolution lives in
// dealerLocationPlantResolver.resolveSubscriberLocationEffectivePlant.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  bulkSetManufacturingSubscriberLocationsPlant,
  listManufacturingDealers,
  listManufacturingSubscriberLocations,
} from "@/lib/manufacturing/dealersClient";
import type { MfgPlant } from "@/lib/manufacturingClient";
import type {
  MfgSubscriberDealer,
  MfgSubscriberLocation,
} from "@/lib/manufacturing/dealerTypes";
import {
  buildDealerOrgPlantId,
  buildPlantById,
  defaultPlantOf,
  resolveSubscriberLocationEffectivePlant,
  type EffectivePlant,
} from "./dealerLocationPlantResolver";

export type SubLocPlantFilter = "" | "__unassigned__" | string;
export type SubLocActiveFilter = "active" | "all";
// Slice 13b: filter chip for `location_type`. "" = All (no filter);
// the other three map 1:1 to the user-visible values:
//   retail    -> walk-in sales lot
//   virtual   -> online-only sales channel ("Online" in the chip)
//   corporate -> back-office / HQ anchor
// Rows with location_type = "plant" or null fall under "All" only —
// intentional per the slice spec; revisit if an "Other" bucket is
// needed for dealer-owned production facilities.
export type SubLocTypeFilter = "" | "retail" | "virtual" | "corporate";

export type PendingSubReassignment = {
  id: string;
  name: string;
  fromPlantLabel: string;
  toPlantLabel: string;
};

type PendingApply = {
  ids: string[];
  targetPlantId: string | null;
  fromBulk: boolean;
};

export const SUB_PLANT_FILTER_UNASSIGNED = "__unassigned__";

function locationState(loc: MfgSubscriberLocation): string {
  return typeof loc.state === "string" ? loc.state.trim() : "";
}

export function useManufacturingSubscriberLocations(
  plants: MfgPlant[],
  refreshSignal: number
) {
  const [subscriberDealers, setSubscriberDealers] = useState<MfgSubscriberDealer[]>([]);
  const [subscriberLocations, setSubscriberLocations] = useState<MfgSubscriberLocation[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [filterPlant, setFilterPlant] = useState<SubLocPlantFilter>("");
  const [filterSubscriberOrg, setFilterSubscriberOrg] = useState<string>("");
  const [filterState, setFilterState] = useState("");
  const [filterActive, setFilterActive] = useState<SubLocActiveFilter>("active");
  const [filterLocationType, setFilterLocationType] = useState<SubLocTypeFilter>("");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPlantId, setBulkPlantId] = useState<string>("");
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [dealersRes, locsRes] = await Promise.all([
      listManufacturingDealers(),
      listManufacturingSubscriberLocations(),
    ]);
    setListBusy(false);
    if (!dealersRes.ok) {
      setLocalError(dealersRes.error);
      return;
    }
    if (!locsRes.ok) {
      setLocalError(locsRes.error);
      return;
    }
    setSubscriberDealers(dealersRes.data.subscriber_dealers);
    setSubscriberLocations(locsRes.data.subscriber_locations);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, refreshSignal]);

  const plantById = useMemo(() => buildPlantById(plants), [plants]);
  const defaultPlant = useMemo(() => defaultPlantOf(plants), [plants]);
  const dealerOrgPlantId = useMemo(
    () => buildDealerOrgPlantId(subscriberDealers),
    [subscriberDealers]
  );
  const plantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of plants) map.set(p.id, `${p.plant_code} · ${p.name}`);
    return map;
  }, [plants]);

  const effectivePlantByLocationId = useMemo(() => {
    const map = new Map<string, EffectivePlant>();
    for (const loc of subscriberLocations) {
      map.set(
        loc.id,
        resolveSubscriberLocationEffectivePlant(loc, plantById, dealerOrgPlantId, defaultPlant)
      );
    }
    return map;
  }, [subscriberLocations, plantById, dealerOrgPlantId, defaultPlant]);
  const effectivePlantFor = useCallback(
    (locId: string) => effectivePlantByLocationId.get(locId),
    [effectivePlantByLocationId]
  );

  const knownStates = useMemo(() => {
    const set = new Set<string>();
    for (const loc of subscriberLocations) {
      const s = locationState(loc);
      if (s) set.add(s.toUpperCase());
    }
    return Array.from(set).sort();
  }, [subscriberLocations]);

  const knownSubscriberOrgs = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of subscriberLocations)
      map.set(loc.subscriber_org_id, loc.subscriber_org_name);
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subscriberLocations]);

  const filteredLocations = useMemo(() => {
    const q = search.trim().toLowerCase();
    const wantState = filterState.trim().toUpperCase();
    return subscriberLocations.filter((loc) => {
      if (filterActive === "active" && !loc.is_active) return false;

      if (filterPlant === SUB_PLANT_FILTER_UNASSIGNED) {
        if (loc.primary_plant_id) return false;
      } else if (filterPlant) {
        if (loc.primary_plant_id !== filterPlant) return false;
      }

      if (filterSubscriberOrg && loc.subscriber_org_id !== filterSubscriberOrg) return false;

      if (filterLocationType && loc.location_type !== filterLocationType) return false;

      if (wantState && locationState(loc).toUpperCase() !== wantState) return false;

      if (q) {
        const haystack = [
          loc.name,
          loc.subscriber_org_name,
          loc.location_code ?? "",
          loc.location_type ?? "",
          loc.address_1 ?? "",
          loc.address_2 ?? "",
          loc.city ?? "",
          loc.state ?? "",
          loc.postal_code ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [
    subscriberLocations,
    search,
    filterPlant,
    filterSubscriberOrg,
    filterState,
    filterActive,
    filterLocationType,
  ]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const loc of filteredLocations) next.add(loc.id);
      return next;
    });
  }, [filteredLocations]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const allVisibleSelected =
    filteredLocations.length > 0 &&
    filteredLocations.every((loc) => selectedIds.has(loc.id));

  const targetPlantId: string | null = bulkPlantId === "" ? null : bulkPlantId;
  const selectedLocations = useMemo(
    () => subscriberLocations.filter((loc) => selectedIds.has(loc.id)),
    [subscriberLocations, selectedIds]
  );

  const previewReassignments = useCallback(
    (ids: string[], target: string | null): PendingSubReassignment[] => {
      const label = (pid: string | null) =>
        pid ? (plantNameById.get(pid) ?? pid.slice(0, 8)) : "(no plant)";
      const toLabel = label(target);
      const idSet = new Set(ids);
      return subscriberLocations
        .filter((loc) => idSet.has(loc.id))
        .filter((loc) => loc.primary_plant_id && loc.primary_plant_id !== target)
        .map((loc) => ({
          id: loc.id,
          name: loc.name,
          fromPlantLabel: label(loc.primary_plant_id),
          toPlantLabel: toLabel,
        }));
    },
    [subscriberLocations, plantNameById]
  );

  const pendingReassignments = useMemo(
    () => previewReassignments(Array.from(selectedIds), targetPlantId),
    [previewReassignments, selectedIds, targetPlantId]
  );

  const netNewAssignmentCount = selectedLocations.filter(
    (loc) => !loc.primary_plant_id && targetPlantId !== null
  ).length;
  const isNoOp =
    selectedLocations.length === 0 ||
    selectedLocations.every((loc) => (loc.primary_plant_id ?? null) === targetPlantId);

  const confirmOpen = pendingApply !== null;
  const confirmView = useMemo(() => {
    if (!pendingApply)
      return { reassignments: [] as PendingSubReassignment[], netNewCount: 0, targetLabel: "" };
    const ids = new Set(pendingApply.ids);
    const t = pendingApply.targetPlantId;
    return {
      reassignments: previewReassignments(pendingApply.ids, t),
      netNewCount: subscriberLocations.filter(
        (loc) => ids.has(loc.id) && !loc.primary_plant_id && t !== null
      ).length,
      targetLabel: t === null ? "(no plant — clear)" : (plantNameById.get(t) ?? t.slice(0, 8)),
    };
  }, [pendingApply, subscriberLocations, plantNameById, previewReassignments]);

  const performApply = useCallback(
    async (apply: PendingApply) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await bulkSetManufacturingSubscriberLocationsPlant({
        location_ids: apply.ids,
        primary_plant_id: apply.targetPlantId,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      if (apply.fromBulk) {
        setSelectedIds(new Set());
        setBulkPlantId("");
      }
      setPendingApply(null);
      await reload();
    },
    [reload]
  );

  const requestBulkApply = useCallback(() => {
    if (selectedLocations.length === 0) return;
    const apply: PendingApply = { ids: Array.from(selectedIds), targetPlantId, fromBulk: true };
    if (pendingReassignments.length > 0) setPendingApply(apply);
    else void performApply(apply);
  }, [selectedLocations.length, selectedIds, targetPlantId, pendingReassignments.length, performApply]);

  const requestRowReassign = useCallback(
    (loc: MfgSubscriberLocation, newPlantId: string | null) => {
      const cur = loc.primary_plant_id ?? null;
      if (cur === newPlantId) return;
      const apply: PendingApply = { ids: [loc.id], targetPlantId: newPlantId, fromBulk: false };
      if (cur !== null) setPendingApply(apply);
      else void performApply(apply);
    },
    [performApply]
  );

  const cancelConfirm = useCallback(() => setPendingApply(null), []);
  const submitBulkApply = useCallback(
    async () => { if (pendingApply) await performApply(pendingApply); },
    [pendingApply, performApply]
  );

  return {
    subscriberDealers, subscriberLocations, plants, plantNameById,
    defaultPlant, effectivePlantFor,
    listBusy, actionBusy, localError, reload,
    search, setSearch,
    filterPlant, setFilterPlant,
    filterSubscriberOrg, setFilterSubscriberOrg,
    filterState, setFilterState,
    filterActive, setFilterActive,
    filterLocationType, setFilterLocationType,
    knownStates, knownSubscriberOrgs, filteredLocations,
    selectedIds, toggleSelected, selectAllFiltered, clearSelection,
    allVisibleSelected, selectedLocations,
    bulkPlantId, setBulkPlantId, targetPlantId,
    pendingReassignments, netNewAssignmentCount, isNoOp,
    requestBulkApply, requestRowReassign,
    confirmOpen,
    confirmReassignments: confirmView.reassignments,
    confirmNetNewCount: confirmView.netNewCount,
    confirmTargetLabel: confirmView.targetLabel,
    cancelConfirm, submitBulkApply,
  };
}

export type UseManufacturingSubscriberLocations = ReturnType<
  typeof useManufacturingSubscriberLocations
>;
